import {Injectable, Injector}           from '@angular/core';
import {Observable, of}                 from 'rxjs';
import {TaskManagementDataProviderBase} from './task-management-data-provider.base';
import {ITaskManagementDataProvider}    from './task-management-data-provider.interface';
import {
  IExportFormatsComboData
}                                       from '../components/wizard/wizard-ui-config/task-management-wizard-details.config';
import {TaskManagementConstants}        from '../task-management.constants';
import {ILegend}                        from '../../stores/map/map.legend.store';
import {PpCommonService}                from '../../plant-protection/services/pp-common.service';
import {CropTypeStore}                  from '../../stores/base-data/crop.types.store';
import {WorkTypes}                      from '../../stores/base-data/worrktypes.store';
import {OperationModesEnum}             from '../../stores/common/operation-modes.store';
import {PP_APPLICATION_LEGEND}          from '../../ap-map/layers/layer.legends';
import {MapLayerTooltipFunction}        from '../../ap-map/layers/map-factory-layer';
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import IPlantProtectionProductList = Data.TaskManagement.IPlantProtectionProductList;

@Injectable({providedIn: 'root'})
export class TaskManagementDataProviderPsfert extends TaskManagementDataProviderBase implements ITaskManagementDataProvider {
  private readonly mapProviderBaseName = 'pro_pp_application_map';
  private readonly mapBaseName = 'map_pp_application_map';

  constructor(private ppCommonService: PpCommonService,
              private cropTypesStore: CropTypeStore,
              injector: Injector) {
    super(injector);
  }

  public getComponentUrl(): string {
    return 'plant-protection/planning';
  }

  public getModuleText(_: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.translationStore.FindTranslationForSelectedLanguage('Task_Mgmt_PS');
  }

  public getOperationModeText(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (!summaryOrPlanBook || !summaryOrPlanBook?.OperationMode?.Key) {
      return null;
    }
    return this.translationStore.FindTranslationForSelectedLanguage(summaryOrPlanBook.OperationMode.Key);
  }

  public getProductName(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.getProductNames(summaryOrPlanBook, false);
  }

  public getProductTooltip(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.getProductNames(summaryOrPlanBook, true);
  }

  public getProductUnit(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook, perHa = false): string {
    if (!this.isSprayingPlanBook(summaryOrPlanBook)) {
      return '';
    }
    // For plant protection the product is water, and therefore we always show l/ha
    return this.translationStore.FindTranslationForSelectedLanguage(perHa ? 'Unit_l_per_ha' : 'Api_Enums__Litre');
  }

  public getApplRate(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!this.isSprayingPlanBook(summaryOrPlanBook)) {
      return undefined;
    }
    return summaryOrPlanBook?.ApplRate;
  }

  public getApplMin(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (this.isSummary(summaryOrPlanBook) || !this.isSprayingPlanBook(summaryOrPlanBook)) {
      return undefined;
    }
    return (summaryOrPlanBook as ITaskManagementPlanBook)?.ApplMin;
  }

  public getApplMax(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (this.isSummary(summaryOrPlanBook) || !this.isSprayingPlanBook(summaryOrPlanBook)) {
      return undefined;
    }
    return (summaryOrPlanBook as ITaskManagementPlanBook)?.ApplMax;
  }

  public getApplSum(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!this.isSprayingPlanBook(summaryOrPlanBook)) {
      return undefined;
    }
    const area = this.getAreaHaSum(summaryOrPlanBook);
    if (area === 0) {
      return undefined;
    }
    return summaryOrPlanBook.ApplRate * area;
  }

  public getExportFormats(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): Array<IExportFormatsComboData> {
    if (!summaryOrPlanBook || !summaryOrPlanBook.OperationMode) {
      return [];
    }
    const exportFormatFilter = [
      TaskManagementConstants.EXPORT_FORMAT_PDF
    ];
    if (summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.PpTarget
      || summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.PpTargetMapPerField
      || summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.PPTargetMapPerPlan) {
      exportFormatFilter.push(TaskManagementConstants.EXPORT_FORMAT_PFBOX_RST);
    }
    if (summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.PpTargetMapPerField
      || summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.PPTargetMapPerPlan) {
      exportFormatFilter.push(TaskManagementConstants.EXPORT_FORMAT_SHP_POLYGON);
      exportFormatFilter.push(TaskManagementConstants.EXPORT_FORMAT_ISO);
    }
    return super.getFilteredExportFormats(exportFormatFilter);
  }

  public getMapFactoryUrl(planBooks: ITaskManagementPlanBook[]): string {
    if (!planBooks || planBooks.length < 1) {
      return '';
    }
    return super.getMapFactoryUrl(planBooks, `${this.mapProviderBaseName}`, `${this.mapBaseName}`);
  }

  public getMapLegend(summary: ITaskManagementPlanSummary): () => ILegend {
    const nMin = summary.PlanBooks?.length > 0 ? Math.min(...summary.PlanBooks?.map(_ => _.ApplMin)) : 0;
    const nMax = summary.PlanBooks?.length > 0 ? Math.max(...summary.PlanBooks?.map(_ => _.ApplMax)) : 0;
    return () => this.convertToTaskMgmtLegend(PP_APPLICATION_LEGEND({Min: nMin, Max: nMax}));
  }

  public getMapLayerTooltip(): MapLayerTooltipFunction {
    return this.mapLayerTooltipStore.getPpApplicationMapLayerTooltip();
  }

  public getExportBaseFilename(summary: ITaskManagementPlanSummary, planBook: ITaskManagementPlanBook): string {
    const applDate = this.dateService.dateToMoment(planBook?.ApplDate ? planBook.ApplDate : new Date()).format('YYMMDD');
    const note = (summary?.Remark ? `${this.textProcessorService.replaceSpecialChars(summary.Remark.substring(0, 20))}` : '');
    let filename = `${applDate}_${(note?.length > 0 ? '_' : '')}${note}`;
    filename += `${this.translationStore.FindTranslationForSelectedLanguage('Task_Mgmt_PS')}`;
    if (planBook.MainCropId > 0) {
      const cropTypeShort = this.translationStore.FindTranslationForSelectedLanguage(
        this.cropTypesStore.getDefaultCroptype(planBook.MainCropId)?.Shortname);
      if (cropTypeShort?.length > 0) {
        filename += `_${cropTypeShort}`;
      }
    }
    return filename;
  }

  public deletePlannings(_: ITaskManagementPlanSummary | ITaskManagementPlanBook[]): void {
  }

  public changesSaveStatus(): Observable<any> {
    return of(true);
  }

  private getProductNames(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook, isTooltip?: boolean): string {
    let planBooks: ITaskManagementPlanBook[];
    if (this.isSummary(summaryOrPlanBook)) {
      planBooks = (summaryOrPlanBook as ITaskManagementPlanSummary)?.PlanBooks;
    } else {
      planBooks = [summaryOrPlanBook as ITaskManagementPlanBook];
    }
    const appliedProducts: IPlantProtectionProductList[] = [];
    for (const productListItem of planBooks.map(pB => pB.PpProductList).flat()) {
      if (!appliedProducts.Any(p => p.ProductListDetails?.Id === productListItem.ProductListDetails?.Id)) {
        appliedProducts.push(productListItem);
      }
    }
    return this.ppCommonService.getAppliedProductsExpr(appliedProducts, isTooltip);
  }

  private isSprayingPlanBook(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): boolean {
    const sourcePlan = this.isSummary(summaryOrPlanBook)
      ? (summaryOrPlanBook as ITaskManagementPlanSummary)?.PlanBooks?.FirstOrDefault()
      : summaryOrPlanBook as ITaskManagementPlanBook;
    return sourcePlan?.PpProductList?.FirstOrDefault()?.ProductListDetails?.WorkType === WorkTypes.Spraying;
  }
}
